import React from 'react';
import { Link } from 'react-router-dom';
import { FaLanguage, FaQuestionCircle, FaBook, FaGlobe, FaHistory, FaUsers, FaMapMarkerAlt, FaGraduationCap, FaLightbulb, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';

const FarsiVsPersian: React.FC = () => {
  return (
    <>
      <SEO 
        title="Farsi vs Persian: What's the Difference? Complete Guide | پیروز Pirooz"
        description="Discover the key differences between Farsi and Persian language. Learn about dialects, regions, and cultural contexts. Complete guide to understanding Farsi vs Persian for language learners."
        keywords={[
          'farsi vs persian', 'farsi vs persian language', 'difference between farsi and persian',
          'persian vs farsi difference', 'farsi persian comparison', 'is farsi persian',
          'farsi language vs persian language', 'persian dialect vs farsi dialect',
          'dari vs farsi vs persian', 'tajik vs farsi vs persian', 'persian language varieties',
          'farsi language history', 'persian language history', 'farsi etymology', 'persian etymology',
          'iranian language', 'persian dialects', 'farsi dialects', 'persian language family',
          'farsi language family', 'indo-iranian languages', 'persian language classification'
        ]}
        canonicalUrl="/farsi-vs-persian"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Farsi vs Persian: Complete Guide to Language Differences",
          "description": "Comprehensive guide explaining the differences between Farsi and Persian language, including dialects, regions, and cultural contexts.",
          "author": {
            "@type": "Organization",
            "name": "پیروز Pirooz"
          },
          "publisher": {
            "@type": "Organization",
            "name": "پیروز Pirooz",
            "logo": {
              "@type": "ImageObject",
              "url": "https://farsi-app.com/logo512.png"
            }
          },
          "datePublished": "2024-12-19",
          "dateModified": "2024-12-19",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://farsi-app.com/farsi-vs-persian"
          }
        }}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-6">
              <FaLanguage className="text-white text-3xl" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
              Farsi vs Persian: What's the Difference?
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              A comprehensive guide to understanding the relationship between Farsi and Persian language, 
              including dialects, regions, and cultural contexts for language learners.
            </p>
          </div>

          {/* Quick Answer */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-8 mb-12">
            <div className="flex items-start">
              <FaQuestionCircle className="text-green-600 text-2xl mt-1 mr-4 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-green-800 mb-4">Quick Answer</h2>
                <p className="text-lg text-green-700 leading-relaxed">
                  <strong>Farsi and Persian refer to the same language.</strong> "Persian" is the English name, 
                  while "Farsi" is the native name used by speakers in Iran. The language has three main dialects: 
                  Farsi (Iran), Dari (Afghanistan), and Tajik (Tajikistan). All three are mutually intelligible 
                  but have regional variations in pronunciation, vocabulary, and script.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Main Article */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                  <FaBook className="text-blue-600 mr-3" />
                  The Complete Guide
                </h2>

                {/* Historical Context */}
                <section className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <FaHistory className="text-purple-600 mr-3" />
                    Historical Context
                  </h3>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      The Persian language has a rich history spanning over 2,500 years. Originally known as 
                      "Parsi" in ancient times, it evolved through several stages: Old Persian (Achaemenid era), 
                      Middle Persian (Sassanian era), and New Persian (Islamic era onwards).
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      The term "Persian" comes from the ancient Greek name for the region, while "Farsi" 
                      is derived from the Arabic pronunciation of "Parsi." In modern times, both terms refer 
                      to the same language, but their usage varies by region and context.
                    </p>
                  </div>
                </section>

                {/* Regional Varieties */}
                <section className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <FaMapMarkerAlt className="text-green-600 mr-3" />
                    Regional Varieties
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                      <h4 className="text-xl font-bold text-blue-800 mb-3">Farsi (Iran)</h4>
                      <ul className="text-blue-700 space-y-2">
                        <li>• Native name: فارسی (Farsi)</li>
                        <li>• Script: Persian-Arabic</li>
                        <li>• Speakers: ~50 million</li>
                        <li>• Official language of Iran</li>
                        <li>• Most widely studied variety</li>
                      </ul>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                      <h4 className="text-xl font-bold text-green-800 mb-3">Dari (Afghanistan)</h4>
                      <ul className="text-green-700 space-y-2">
                        <li>• Native name: دری (Dari)</li>
                        <li>• Script: Persian-Arabic</li>
                        <li>• Speakers: ~15 million</li>
                        <li>• Official language of Afghanistan</li>
                        <li>• More conservative vocabulary</li>
                      </ul>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                      <h4 className="text-xl font-bold text-purple-800 mb-3">Tajik (Tajikistan)</h4>
                      <ul className="text-purple-700 space-y-2">
                        <li>• Native name: Тоҷикӣ (Tojiki)</li>
                        <li>• Script: Cyrillic (also Latin)</li>
                        <li>• Speakers: ~8 million</li>
                        <li>• Official language of Tajikistan</li>
                        <li>• Russian influence</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Key Differences */}
                <section className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <FaLightbulb className="text-yellow-600 mr-3" />
                    Key Differences
                  </h3>
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-gray-800 mb-3">Pronunciation</h4>
                      <p className="text-gray-700">
                        While mutually intelligible, each variety has distinct pronunciation patterns. 
                        Farsi tends to be more conservative in vowel sounds, while Dari has more 
                        Arabic influence, and Tajik shows Russian phonetic influence.
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-gray-800 mb-3">Vocabulary</h4>
                      <p className="text-gray-700">
                        All varieties share core vocabulary, but regional differences exist. 
                        Farsi has more French loanwords, Dari retains more Arabic terms, 
                        and Tajik incorporates Russian vocabulary.
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-gray-800 mb-3">Script</h4>
                      <p className="text-gray-700">
                        Farsi and Dari use the Persian-Arabic script, while Tajik primarily 
                        uses Cyrillic script (though Persian script is also used). This 
                        affects learning approaches for each variety.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Which Should You Learn? */}
                <section className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <FaGraduationCap className="text-indigo-600 mr-3" />
                    Which Should You Learn?
                  </h3>
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      For most learners, <strong>Farsi (Iranian Persian)</strong> is the best choice because:
                    </p>
                    <ul className="text-gray-700 space-y-2 mb-4">
                      <li>• Most widely taught and studied variety</li>
                      <li>• Largest number of speakers</li>
                      <li>• Most learning resources available</li>
                      <li>• Easier to find native speakers for practice</li>
                      <li>• Access to Iranian culture, literature, and media</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed">
                      However, choose Dari if you're specifically interested in Afghanistan 
                      or Tajik if you're focusing on Central Asia.
                    </p>
                  </div>
                </section>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Facts */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Quick Facts</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Speakers:</span>
                    <span className="font-semibold">~70 million</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Language Family:</span>
                    <span className="font-semibold">Indo-Iranian</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Writing System:</span>
                    <span className="font-semibold">Persian-Arabic</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mutual Intelligibility:</span>
                    <span className="font-semibold">High (90%+)</span>
                  </div>
                </div>
              </div>

              {/* Related Learning */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Continue Learning</h3>
                <div className="space-y-3">
                  <Link 
                    to="/learn-farsi-fast" 
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <span className="text-blue-700 font-medium">Learn Farsi Fast</span>
                    <FaArrowRight className="text-blue-600" />
                  </Link>
                  <Link 
                    to="/alphabet-learning" 
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <span className="text-green-700 font-medium">Persian Alphabet</span>
                    <FaArrowRight className="text-green-600" />
                  </Link>
                  <Link 
                    to="/pathways" 
                    className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    <span className="text-purple-700 font-medium">Learning Pathways</span>
                    <FaArrowRight className="text-purple-600" />
                  </Link>
                </div>
              </div>

              {/* FAQ */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Frequently Asked</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Are Farsi and Persian the same?</h4>
                    <p className="text-sm text-gray-600">Yes, they refer to the same language. "Persian" is the English name, "Farsi" is the native name.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Which is easier to learn?</h4>
                    <p className="text-sm text-gray-600">All varieties have similar difficulty. Farsi has the most learning resources available.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Can speakers understand each other?</h4>
                    <p className="text-sm text-gray-600">Yes, speakers of all three varieties can communicate with 90%+ mutual intelligibility.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Learning Farsi?</h2>
            <p className="text-xl mb-6 opacity-90">
              Join thousands of learners mastering Persian language with our comprehensive courses
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/learn-farsi-fast" 
                className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
              >
                Start Learning Now
              </Link>
              <Link 
                to="/pathways" 
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Explore Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FarsiVsPersian;
